import type { Dock, Opening, OpeningType, ZoneCategory } from './types.js';
import { inEveryLocale, modelTexts } from './texts.js';

export interface OpeningPreset {
  readonly type: OpeningType;
  readonly label: string;
  readonly width: number;
  readonly height: number;
  readonly sillHeight: number;
}

const opening = (
  type: OpeningType,
  width: number,
  height: number,
  sillHeight: number,
): OpeningPreset => ({
  type,
  get label() {
    return modelTexts().openings[type];
  },
  width,
  height,
  sillHeight,
});

/** Typical dimensions as starting values only; every value is editable afterwards. */
export const OPENING_CATALOG: readonly OpeningPreset[] = [
  opening('sectional-door', 4, 4.2, 0),
  opening('rolling-door', 5, 5, 0),
  opening('rapid-door', 4, 4.2, 0),
  // Same sizes as the truck gates, smaller ones for passages; the leaf parks beside it.
  opening('fire-gate', 4, 4.2, 0),
  opening('door', 1, 2.1, 0),
  opening('double-door', 2, 2.1, 0),
  opening('glass-door', 1.1, 2.1, 0),
  opening('glass-double-door', 2, 2.1, 0),
  opening('passage', 1, 2.1, 0),
  opening('double-passage', 2, 2.1, 0),
  opening('window', 1.5, 1.25, 1.2),
  opening('window-band', 10, 1, 2.5),
  // Gate at floor level; the truck stands in the depressed approach (see DEFAULT_DOCK).
  opening('loading-dock', 3, 3, 0),
  // Open front (arcade, open hall front); the wall above stays.
  opening('void', 6, 4, 0),
];

/**
 * Depressed dock approach following common manufacturer guidance (e.g. Stertil, Hörmann):
 * 1.2 m deep (truck bed height), at least 4 m wide and 16 m of level standing area so the
 * whole trailer stands level, then a 12 m ramp (10 % slope); leveler 2.0 × 3.0 m inside.
 */
export const DEFAULT_DOCK: Dock = {
  pitDepth: 1.2,
  bayWidth: 4,
  bayLength: 16,
  rampLength: 12,
  levelerWidth: 2,
  levelerLength: 3,
  approach: true,
  approachOffset: 0,
  railingLeft: true,
  railingRight: true,
};

/** Planning minimums/maximums that trigger a warning. */
export const DOCK_MIN_BAY_WIDTH = 4;
export const DOCK_MIN_BAY_LENGTH = 16;
/** Steeper approaches let the trailer top hit the wall and loads topple. */
export const DOCK_MAX_RAMP_SLOPE = 0.1;
/** Center-to-center distance of neighbouring dock gates. */
export const DOCK_GATE_SPACING = 4;

export function openingPreset(type: OpeningType): OpeningPreset {
  const preset = OPENING_CATALOG.find((p) => p.type === type);
  if (!preset) throw new Error(`Unbekannter Öffnungstyp ${type}`);
  return preset;
}

export const isWindowType = (t: OpeningType): boolean => t === 'window' || t === 'window-band';
export const isGateType = (t: OpeningType): boolean =>
  t === 'sectional-door' ||
  t === 'rolling-door' ||
  t === 'rapid-door' ||
  t === 'loading-dock' ||
  t === 'fire-gate';
/** Gates mounted on one face of the wall, which can be switched to the other face. */
export const isFaceMountedType = (t: OpeningType): boolean =>
  t === 'fire-gate' || t === 'rapid-door';

/**
 * Hardware of a high-speed door on its mounting face (metres): side guides reaching `guideInset`
 * into the opening, and the hood over the coil reaching `hoodOverhang` past both edges.
 */
export const RAPID_DOOR_PLAN = {
  guide: 0.16,
  guideInset: 0.05,
  hoodDepth: 0.5,
  hoodHeight: 0.55,
  hoodOverhang: 0.15,
} as const;

/** Transparent vision strip of a high-speed door curtain, metres above the floor. */
export const RAPID_DOOR_VISION = { from: 1.4, to: 2 } as const;

/**
 * Vision strip of a high-speed door (bottom and top above its sill): 1.40–2.00 m, kept
 * inside low doors with the bottom bar and the coil below and above.
 */
export function rapidDoorVision(height: number): { from: number; to: number } | null {
  const top = Math.min(RAPID_DOOR_VISION.to, height - 0.3);
  const from = Math.min(RAPID_DOOR_VISION.from, top - 0.3);
  return from >= 0.25 ? { from, to: top } : null;
}

/**
 * Wall left beside a sliding gate for its open leaf: on the side it slides to, from the
 * opening to the wall end (other openings not counted).
 */
export function slideRoom(
  o: Pick<Opening, 'offsetFromWallStart' | 'width' | 'slideTo'>,
  wallLength: number,
): number {
  return o.slideTo === 'end' ? wallLength - o.offsetFromWallStart - o.width : o.offsetFromWallStart;
}
export const isGlassDoorType = (t: OpeningType): boolean =>
  t === 'glass-door' || t === 'glass-double-door';
/** Wall openings of door size without leaf or frame (Wanddurchbruch). */
export const isPassageType = (t: OpeningType): boolean => t === 'passage' || t === 'double-passage';
/** Hinged doors (steel or glass), which have a swing direction. */
export const isDoorType = (t: OpeningType): boolean =>
  t === 'door' || t === 'double-door' || isGlassDoorType(t);
export const doorLeafCount = (t: OpeningType): 1 | 2 =>
  t === 'double-door' || t === 'glass-double-door' ? 2 : 1;

export interface ZoneCategoryInfo {
  readonly id: ZoneCategory;
  readonly label: string;
  readonly color: string;
  /**
   * Traffic/movement areas are listed separately from functional areas; surfaces are outdoor
   * pavings and green areas drawn on the site.
   */
  readonly group: ZoneGroup;
}

export type ZoneGroup = 'function' | 'traffic' | 'surface';
export const ZONE_GROUPS: readonly ZoneGroup[] = ['function', 'traffic', 'surface'];

const zone = (id: ZoneCategory, color: string, group: ZoneGroup): ZoneCategoryInfo => ({
  id,
  get label() {
    return modelTexts().zoneCategories[id];
  },
  color,
  group,
});

export const ZONE_CATEGORIES: readonly ZoneCategoryInfo[] = [
  zone('production', '#4f8cff', 'function'),
  zone('storage', '#f2a93b', 'function'),
  zone('goods-in', '#2bb3a3', 'function'),
  zone('goods-out', '#1f9d6b', 'function'),
  zone('assembly', '#7a6cf0', 'function'),
  zone('packaging', '#c77dff', 'function'),
  zone('office', '#e5738a', 'function'),
  zone('social', '#f08c6c', 'function'),
  zone('technical', '#8a96a8', 'function'),
  zone('workshop', '#b5835a', 'function'),
  zone('open-space', '#a9c46c', 'function'),
  zone('truck-access', '#6b7785', 'traffic'),
  zone('forklift-path', '#e8c547', 'traffic'),
  zone('walkway', '#9ad0ec', 'traffic'),
  zone('escape-route', '#3fbf5f', 'traffic'),
  zone('work-area', '#ff9f68', 'function'),
  zone('paving', '#b9a896', 'surface'),
  zone('green', '#7fb96b', 'surface'),
  zone('gravel', '#dcd2b8', 'surface'),
  zone('crushed-stone', '#a3a09a', 'surface'),
  zone('asphalt', '#5d6166', 'surface'),
  zone('concrete', '#c6c9ce', 'surface'),
  zone('other', '#b0b8c4', 'function'),
];

export const zoneGroupLabel = (group: ZoneGroup): string => modelTexts().zoneGroups[group];

/** Surfaces cover the ground completely, so they are drawn opaque instead of tinted. */
export const isSurfaceCategory = (id: ZoneCategory): boolean =>
  zoneCategory(id).group === 'surface';

export function zoneCategory(id: ZoneCategory): ZoneCategoryInfo {
  return ZONE_CATEGORIES.find((c) => c.id === id) ?? (ZONE_CATEGORIES.at(-1) as ZoneCategoryInfo);
}

/** True if `name` is the category's default name in any language. */
export const isDefaultZoneName = (category: ZoneCategory, name: string): boolean =>
  inEveryLocale((t) => t.zoneCategories[category]).includes(name);
